import { useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ApolloClient, useApolloClient } from '@apollo/client'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Access } from 'modules/core/hooks/useCurrentSession'

import {
  FormMembershipForTaskDocument,
  InboxTaskByResourceDocument,
  InboxTaskListItemCompletionFragment,
  InboxTaskListItemFragment,
} from 'generated/resources/graphql'

import { useTaskListItemUrl } from 'modules/inbox_tasks/lib/helpers'

/**
 * Decodes a Base64-encoded task reference token into its resource type and ID.
 * The token format is `btoa("ResourceType|resourceId")`.
 *
 * Returns `null` if the token is missing, malformed, or doesn't contain
 * both a resource type and a resource ID.
 */
export const decodeTaskRef = (token: string | null): TaskResourceRef | null => {
  if (!token) return null

  try {
    const decoded = atob(token)
    const separatorIndex = decoded.indexOf('|')
    if (separatorIndex === -1) return null

    const resourceType = decoded.slice(0, separatorIndex)
    const resourceId = decoded.slice(separatorIndex + 1)
    if (!resourceType || !resourceId) return null

    return { resourceType, resourceId }
  } catch {
    return null
  }
}

/**
 * A resource resolver transforms a decoded task reference (resourceType, resourceId)
 * into the actual pair that maps to a task.
 *
 * Return `null` if resolution fails (e.g. no membership found).
 */
export type TaskResourceRef = { resourceType: string; resourceId: string }

type ResourceResolver = (params: {
  resourceId: string
  access: Access
  client: ApolloClient<object>
}) => Promise<TaskResourceRef | null>

/**
 * Registry of resource type resolvers.
 *
 * Most resource types map directly to a task — those don't need an entry here
 * and will use the default path (query inboxTasksConnection with the URL params as-is).
 *
 * Add an entry only when the resource type needs an intermediate
 * resolution step before looking up the task. For example, Forms::Form needs to
 * first resolve the current user's membership, then look up the task by that membership.
 *
 * To add a new resolver:
 *   1. Add a GraphQL query to queries.graphql if needed
 *   2. Run `pnpm graphql-codegen` in frontend/
 *   3. Add the resolver function to this map
 */
export const RESOURCE_RESOLVERS: Record<string, ResourceResolver> = {
  'Forms::Form': async ({ resourceId, access, client }) => {
    const { data } = await client.query({
      query: FormMembershipForTaskDocument,
      variables: {
        formId: resourceId,
        respondentId: access.id.toString(),
      },
    })

    const membershipId = data?.forms?.membershipsConnection?.nodes?.[0]?.id
    if (!membershipId) return null

    return { resourceType: 'Forms::Membership', resourceId: membershipId }
  },
}

type InboxTask = InboxTaskListItemFragment & InboxTaskListItemCompletionFragment

/**
 * Fetches the full inbox task for a given resource reference.
 * Returns the task with all fragment data needed for URL generation.
 */
const fetchTask = async (
  client: ApolloClient<object>,
  ref: TaskResourceRef
): Promise<InboxTask | null> => {
  const { data } = await client.query({
    query: InboxTaskByResourceDocument,
    variables: { ...ref, statuses: ['todo', 'in_progress'] },
  })

  return data?.tasks?.inboxTasksConnection?.nodes?.[0] ?? null
}

/**
 * Reads the `task_ref` query param from the URL (a Base64-encoded token
 * containing `resourceType|resourceId`), resolves it into an inbox task,
 * then navigates to the correct activity for that task.
 *
 * Resolution flow:
 *   1. Read `task_ref` from URL search params and decode via `decodeTaskRef`
 *   2. If a custom resolver exists in RESOURCE_RESOLVERS → run it to get the
 *      actual (resourceType, resourceId) that maps to a task
 *   3. Otherwise use the decoded params directly as the task resource reference
 *   4. Query inboxTasksConnection to find the task (with full fragment data)
 *   5. Use getTaskListItemUrl to resolve the correct activity URL based on
 *      the task's taskable type (e.g. forms.answerForm for forms, core.task
 *      for tasks without a custom getUrl)
 *   6. Navigate to the resolved URL
 */
const useOpenTaskFromParams = () => {
  const [searchParams] = useSearchParams()
  const { access } = useCurrentSession()
  const client = useApolloClient()
  const navigate = useNavigate()
  const { getTaskListItemUrl } = useTaskListItemUrl()
  const resolvedRef = useRef(false)

  const taskRefToken = searchParams.get('task_ref')
  const decoded = decodeTaskRef(taskRefToken)
  const resourceType = decoded?.resourceType ?? null
  const resourceId = decoded?.resourceId ?? null

  useEffect(() => {
    resolvedRef.current = false
  }, [resourceType, resourceId])

  useEffect(() => {
    if (!resourceType || !resourceId || resolvedRef.current) return
    resolvedRef.current = true

    const resolve = async () => {
      try {
        // Resolve the task resource reference
        const resolver = RESOURCE_RESOLVERS[resourceType]

        const taskRef = resolver
          ? await resolver({ resourceId, access, client })
          : { resourceType, resourceId }

        if (!taskRef) return

        // Fetch the full task data
        const task = await fetchTask(client, taskRef)
        if (!task) return

        // Navigate to the correct activity using the task definition system.
        // This delegates to each TaskDefinition's getUrl() — e.g. FillFormTaskDefinition
        // opens forms.answerForm. Tasks without a custom getUrl fall back to core.task.
        const url = getTaskListItemUrl(task)
        navigate(url)
      } catch {
        // Resolution failed — silently ignore so the page remains usable
      }
    }

    void resolve()
  }, [resourceType, resourceId]) // eslint-disable-line react-hooks/exhaustive-deps
}

export default useOpenTaskFromParams
